import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Modal,
    Paper,
    Slide,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import React, { useEffect, useRef, useState } from 'react'
import { Container } from '../../components'

import { UserDrawerProps } from '.'
import {
    applyInvalidPhoneFieldErrors,
    emailHelperText,
    formatPhoneForDisplay,
    INVALID_PHONE_MESSAGE,
    isAccountManager,
    isEmailValid,
    isInvalidPhoneError,
    toMMDDYYYY,
} from '../../helpers'
import {
    INVOICE_CODE_STEP,
    useVendorInvoice,
} from '../../contexts/useVendorInvoice'
import { VendorInvoiceCodeGate } from '../Invoice/VendorInvoiceCodeGate'
import { useUser } from '../../hooks'
import {
    archiveUser,
    CreateUserRequest,
    RootState,
    setWorkSpaceUser,
    UpdateUserRequest,
} from '../../store'
import { toast } from 'react-toastify'
import { CalendarTodayOutlined } from '@material-ui/icons'
import {
    defaultPermissible,
    EDIT_PERMISSION_ROLES,
    EDIT_USER,
    EDIT_USER_PERMISSIONS,
    getCleanPermissible,
    PAYROLL_ACCESS,
    Permissible,
    PermissionRole,
    PERMISSION_FALSE,
    PERMISSION_TRUE,
} from '../../models/Permissions'
import { hasPermission } from '../../models/Users/services'
import { useDispatch, useSelector } from 'react-redux'
import CloseIcon from '@material-ui/icons/Close'
import { typographyStyle } from '../../theme/typographyStyle'

export const UserModal = (props: UserDrawerProps) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const permissionRoles = useSelector(
        (state: RootState) => state.permission.permissionRoleList,
    )
    const { workspaceUser, actions } = useUser()
    const { createUser, updateUser, logout } = actions
    const vendorInvoiceController = useVendorInvoice()

    // user state
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [cellPhone, setCellPhone] = useState('')
    const [userPermissionRoles, setUserPermissionRoles] = useState<number[]>([])
    const [date, setDate] = useState<Date | null>(null)
    const [autoArchiveDate, setAutoArchiveDate] = useState<string>('')

    // ui management state
    const [doValidate, setDoValidate] = useState(false)
    const [phoneError, setPhoneError] = useState(false)
    const [cellPhoneError, setCellPhoneError] = useState(false)

    // permissions state
    const [updatedPermissible, setUpdatedPermissible] = useState<Permissible>(
        defaultPermissible,
    )

    // property manager (apartment workspace only)
    const [markAsPropertyManager, setMarkAsPropertyManager] = useState(false)

    // sub vendor (vendor workspace only)
    const [markAsSubVendor, setMarkAsSubVendor] = useState(false)
    const [showInvoiceCodeDialog, setShowInvoiceCodeDialog] = useState(false)
    const [pendingSubVendorValue, setPendingSubVendorValue] = useState(false)

    // Add loading state
    const [isLoading, setIsLoading] = useState(false)
    const [roleTooltipId, setRoleTooltipId] = useState<number | null>(null)
    const roleListRef = useRef<HTMLDivElement>(null)

    const { open, drawerUser, handleClose } = props

    useEffect(() => {
        if (!open) {
            setRoleTooltipId(null)
        }
    }, [open])

    useEffect(() => {
        const el = roleListRef.current
        if (!el) {
            return
        }
        const closeTooltip = () => setRoleTooltipId(null)
        el.addEventListener('scroll', closeTooltip, { passive: true })
        return () => el.removeEventListener('scroll', closeTooltip)
    }, [open, permissionRoles])

    useEffect(() => {
        setName('')
        setEmail('')
        setPhone('')
        setCellPhone('')
        setUserPermissionRoles([])
        setDate(null)
        setAutoArchiveDate('')
        setMarkAsPropertyManager(false)
        setMarkAsSubVendor(false)
    }, [handleClose])

    useEffect(() => {
        //on user change, do this
        if (drawerUser) {
            setUpdatedPermissible({ ...drawerUser })
            setName(drawerUser.name)
            setEmail(drawerUser.email)
            setDate(drawerUser.created_date)
            if (drawerUser.phone)
                setPhone(formatPhoneForDisplay(drawerUser.phone))
            if (drawerUser.cell_phone) {
                setCellPhone(formatPhoneForDisplay(drawerUser.cell_phone))
            }
            setUserPermissionRoles(
                drawerUser.permission_roles.map((role) => role.id),
            )
            // auto_archive_date comes back as "YYYY-MM-DD" string or null
            setAutoArchiveDate(drawerUser.auto_archive_date ?? '')
            setMarkAsSubVendor(drawerUser.is_sub_vendor ?? false)
        } else {
            setName('')
            setEmail('')
            setPhone('')
            setCellPhone('')
            setUserPermissionRoles([])
            setDate(null)
            setAutoArchiveDate('')
            setMarkAsPropertyManager(false)
            setMarkAsSubVendor(false)
        }
        setDoValidate(false)
    }, [drawerUser, open])

    const inputStyle: React.CSSProperties = {
        margin: theme.spacing(1, 2, 1, 2),
    }

    const roleHasPayrollAccess = (role: PermissionRole) =>
        Number(role.payroll_access) === PERMISSION_TRUE ||
        Number(role[PAYROLL_ACCESS]) === PERMISSION_TRUE

    const canAssignPayrollRoles =
        hasPermission(workspaceUser, PAYROLL_ACCESS) ||
        (workspaceUser?.permission_roles ?? []).some((userRole) => {
            const fullRole = permissionRoles?.find((r) => r.id === userRole.id)
            return fullRole
                ? roleHasPayrollAccess(fullRole)
                : roleHasPayrollAccess(userRole)
        })

    const roleHasEditPermissionRolesAccess = (role: PermissionRole) =>
        Number(role.edit_permission_roles) === PERMISSION_TRUE ||
        Number(role[EDIT_PERMISSION_ROLES]) === PERMISSION_TRUE

    const canAssignEditPermissionRoles =
        hasPermission(workspaceUser, EDIT_PERMISSION_ROLES) ||
        (workspaceUser?.permission_roles ?? []).some((userRole) => {
            const fullRole = permissionRoles?.find((r) => r.id === userRole.id)
            return fullRole
                ? roleHasEditPermissionRolesAccess(fullRole)
                : roleHasEditPermissionRolesAccess(userRole)
        })

    const filterAssignableRoleIds = (roleIds: number[]) => {
        if (!permissionRoles?.length) {
            return roleIds
        }
        return roleIds.filter((id) => {
            const role = permissionRoles.find((r) => r.id === id)
            if (!role) {
                return true
            }
            if (roleHasPayrollAccess(role) && !canAssignPayrollRoles) {
                return false
            }
            if (
                roleHasEditPermissionRolesAccess(role) &&
                !canAssignEditPermissionRoles
            ) {
                return false
            }
            return true
        })
    }

    const saveButtonClick = () => {
        // first validate the name
        if (name === '') {
            setDoValidate(true)
            return
        }
        setIsLoading(true)
        if (drawerUser) {
            //update the user
            //first create the req body
            const req: UpdateUserRequest = {
                isCurrentUser: workspaceUser?.id === drawerUser.id,
                params: {
                    id: drawerUser.id,
                },
                body: {
                    name: name,
                    phone: phone,
                    cell_phone: cellPhone,
                    email: email,
                    // Send null to clear, or the date string if set
                    auto_archive_date: autoArchiveDate || null,
                },
            }

            if (markAsPropertyManager) {
                req.body.is_property_manager = true
            }
            if (markAsSubVendor !== (drawerUser.is_sub_vendor ?? false)) {
                req.body.is_sub_vendor = markAsSubVendor
            }
            if (hasPermission(workspaceUser, EDIT_USER_PERMISSIONS)) {
                let permissions = getCleanPermissible(updatedPermissible)
                if (!canAssignPayrollRoles) {
                    permissions = {
                        ...permissions,
                        payroll_access: PERMISSION_FALSE,
                    }
                }
                if (!canAssignEditPermissionRoles) {
                    permissions = {
                        ...permissions,
                        edit_permission_roles: PERMISSION_FALSE,
                    }
                }
                req.body.permissions = permissions
                req.body.permission_roles = filterAssignableRoleIds(
                    userPermissionRoles,
                )
            } else if (
                (canAssignPayrollRoles || canAssignEditPermissionRoles) &&
                permissionRoles
            ) {
                const modifiableRoleIds = new Set(
                    permissionRoles
                        .filter(
                            (r) =>
                                (roleHasPayrollAccess(r) &&
                                    canAssignPayrollRoles) ||
                                (roleHasEditPermissionRolesAccess(r) &&
                                    canAssignEditPermissionRoles),
                        )
                        .map((r) => r.id),
                )
                const existingUnmodifiableRoleIds = drawerUser.permission_roles
                    .map((r) => r.id)
                    .filter((id) => !modifiableRoleIds.has(id))
                const selectedModifiableRoleIds = userPermissionRoles.filter(
                    (id) => modifiableRoleIds.has(id),
                )
                req.body.permission_roles = [
                    ...existingUnmodifiableRoleIds,
                    ...selectedModifiableRoleIds,
                ]
            }

            updateUser(req)
                .then((response) => {
                    toast.success(`${name} was updated!`)
                    if (
                        markAsPropertyManager &&
                        workspaceUser?.active_workspace
                    ) {
                        const updatedUser = {
                            ...workspaceUser,
                            active_workspace: {
                                ...workspaceUser.active_workspace,
                                property_manager: response.data,
                            },
                        }
                        dispatch(setWorkSpaceUser(updatedUser))
                    }
                    setIsLoading(false)
                    handleClose()
                })
                .catch((e) => {
                    setIsLoading(false)
                    if (isInvalidPhoneError(e)) {
                        applyInvalidPhoneFieldErrors({
                            phone,
                            cellPhone,
                            setPhoneError,
                            setCellPhoneError,
                        })
                    }
                })
        } else {
            //create a new user
            if (!isEmailValid(email)) {
                //this isn't validating they aren't in the system, this just validates the format using a regex
                setDoValidate(true)
                setIsLoading(false)
                return
            }
            let roleIds = filterAssignableRoleIds(userPermissionRoles)
            if (!hasPermission(workspaceUser, EDIT_USER_PERMISSIONS)) {
                const restrictedRoleIds = new Set(
                    (permissionRoles ?? [])
                        .filter(
                            (r) =>
                                roleHasPayrollAccess(r) ||
                                roleHasEditPermissionRolesAccess(r),
                        )
                        .map((r) => r.id),
                )
                roleIds = roleIds.filter((id) => restrictedRoleIds.has(id))
            }
            const req: CreateUserRequest = {
                body: {
                    email: email,
                    name: name,
                    phone: phone,
                    cell_phone: cellPhone,
                    permission_roles: roleIds,
                    ...(markAsPropertyManager && { is_property_manager: true }),
                    ...(markAsSubVendor && { is_sub_vendor: true }),
                    auto_archive_date: autoArchiveDate || null,
                },
            }

            //not validating that they aren't creating a user with a duplicate email because the toast will alert the user
            createUser(req)
                .then((response) => {
                    toast.success(`${name} was added to your workspace!`)
                    if (
                        markAsPropertyManager &&
                        workspaceUser?.active_workspace
                    ) {
                        const updatedUser = {
                            ...workspaceUser,
                            active_workspace: {
                                ...workspaceUser.active_workspace,
                                property_manager: response.data,
                            },
                        }
                        dispatch(setWorkSpaceUser(updatedUser))
                    }
                    setIsLoading(false)
                    handleClose()
                })
                .catch((e) => {
                    setIsLoading(false)
                    if (isInvalidPhoneError(e)) {
                        applyInvalidPhoneFieldErrors({
                            phone,
                            cellPhone,
                            setPhoneError,
                            setCellPhoneError,
                        })
                    }
                })
        } // end if else chain
    }

    const DRAWER_USER_IS_WORKSPACE_OWNER = drawerUser?.workspace_owner
    const DRAWER_USER_IS_NOT_SELF =
        drawerUser && drawerUser.id !== workspaceUser?.id
    const USER_CAN_EDIT_USER_PERMISSIONS = hasPermission(
        workspaceUser,
        EDIT_USER_PERMISSIONS,
    )

    const isWorkspaceOwnerLocked = Boolean(
        DRAWER_USER_IS_WORKSPACE_OWNER && DRAWER_USER_IS_NOT_SELF,
    )

    const isRoleCheckboxDisabled = (role: PermissionRole) => {
        if (isWorkspaceOwnerLocked) {
            return true
        }
        if (roleHasPayrollAccess(role)) {
            return !canAssignPayrollRoles
        }
        if (roleHasEditPermissionRolesAccess(role)) {
            return !canAssignEditPermissionRoles
        }
        return !USER_CAN_EDIT_USER_PERMISSIONS
    }

    const getRoleCheckboxTooltip = (role: PermissionRole) => {
        if (isWorkspaceOwnerLocked) {
            return 'Cannot change roles for the workspace owner'
        }
        if (roleHasPayrollAccess(role) && !canAssignPayrollRoles) {
            return 'You need Payroll Access to assign roles with Payroll Access'
        }
        if (
            roleHasEditPermissionRolesAccess(role) &&
            !canAssignEditPermissionRoles
        ) {
            return 'You need Edit Permission Roles to assign roles with Edit Permission Roles'
        }
        if (
            !roleHasPayrollAccess(role) &&
            !roleHasEditPermissionRolesAccess(role) &&
            !USER_CAN_EDIT_USER_PERMISSIONS
        ) {
            return 'You do not have permission to assign roles'
        }
        return ''
    }

    const renderPermissionRoles = (
        permissionRoles: PermissionRole[] | undefined,
    ) => {
        return permissionRoles?.map((role) => {
            const isRoleDisabled = isRoleCheckboxDisabled(role)
            const tooltip = getRoleCheckboxTooltip(role)
            const showTooltip = Boolean(tooltip) && isRoleDisabled

            const row = (
                <Container
                    style={{ alignItems: 'center', width: '100%' }}
                    onClick={() => {
                        setRoleTooltipId(null)
                        if (!isRoleDisabled) {
                            if (userPermissionRoles.includes(role.id)) {
                                setUserPermissionRoles(
                                    userPermissionRoles.filter((r) => {
                                        return r != role.id
                                    }),
                                )
                            } else {
                                setUserPermissionRoles([
                                    ...userPermissionRoles,
                                    role.id,
                                ])
                            }
                        }
                    }}
                >
                    <Container style={{ flex: 1 }}>{role.name}</Container>
                    <span>
                        <Checkbox
                            checked={userPermissionRoles.includes(role.id)}
                            disabled={isRoleDisabled}
                            style={{
                                color: isRoleDisabled
                                    ? undefined
                                    : theme.palette.primary.dark,
                            }}
                        />
                    </span>
                </Container>
            )

            if (!showTooltip) {
                return <React.Fragment key={role.id}>{row}</React.Fragment>
            }

            return (
                <div key={role.id} style={{ width: '100%' }}>
                    <Tooltip
                        title={tooltip}
                        open={roleTooltipId === role.id}
                        disableHoverListener
                        disableFocusListener
                        disableTouchListener
                        leaveDelay={0}
                        PopperProps={{ disablePortal: true }}
                    >
                        <span
                            style={{
                                display: 'block',
                                width: '100%',
                            }}
                            onMouseEnter={() => setRoleTooltipId(role.id)}
                            onMouseLeave={() => setRoleTooltipId(null)}
                        >
                            {row}
                        </span>
                    </Tooltip>
                </div>
            )
        })
    }

    const disableEmail =
        workspaceUser && drawerUser
            ? (hasPermission(workspaceUser, EDIT_USER) &&
                  workspaceUser.id === drawerUser.id) ||
              isAccountManager(workspaceUser)
            : hasPermission(workspaceUser, EDIT_USER)

    let canSave = true

    if (drawerUser) {
        canSave = hasPermission(workspaceUser, EDIT_USER)
    }

    const isInvoiceCodeValidated =
        vendorInvoiceController.step !== INVOICE_CODE_STEP

    return (
        <>
            <Modal
                open={open}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                onClose={() => {
                    handleClose()
                }}
            >
                <Slide direction="up" in={open}>
                    <Paper
                        style={{
                            display: 'flex',
                            overflow: 'hidden',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            outline: 'none',
                            borderRadius: '20px',
                        }}
                    >
                        <Container
                            style={{ margin: theme.spacing(3) }}
                            direction="column"
                        >
                            <Container style={{ flex: 1 }}>
                                {(drawerUser && (
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.h5,
                                            ),
                                            fontWeight:
                                                theme.typography.fontWeightBold,
                                            marginTop: theme.spacing(1),
                                            marginLeft: theme.spacing(4),
                                        }}
                                    >
                                        {name}
                                    </span>
                                )) || (
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.h5,
                                            ),
                                            fontWeight:
                                                theme.typography.fontWeightBold,
                                            marginTop: theme.spacing(1),
                                            marginLeft: theme.spacing(4),
                                        }}
                                    >
                                        Add New Member
                                    </span>
                                )}
                                <div style={{ flex: 1 }} />
                                <Tooltip title="Cancel">
                                    <IconButton onClick={handleClose}>
                                        <CloseIcon
                                            fontSize="large"
                                            style={{
                                                marginRight: theme.spacing(1),
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Container>
                            <Divider
                                style={{ margin: theme.spacing(0, 3, 0, 4) }}
                            />

                            <Container
                                direction="row"
                                style={{ margin: theme.spacing(2) }}
                            >
                                {/* body */}
                                <Container
                                    direction="column"
                                    flex={1}
                                    scrollY
                                    style={{ width: '350px' }}
                                >
                                    {(drawerUser && (
                                        <Container
                                            style={{
                                                margin: theme.spacing(
                                                    1,
                                                    2,
                                                    1,
                                                    2,
                                                ),
                                                color: 'grey',
                                            }}
                                        >
                                            You&apos;re reviewing the details
                                            for {drawerUser.name} down below!
                                        </Container>
                                    )) || (
                                        <Container
                                            style={{
                                                margin: theme.spacing(
                                                    1,
                                                    2,
                                                    1,
                                                    2,
                                                ),
                                                color: 'grey',
                                            }}
                                        >
                                            Please enter your teammates details
                                            below!
                                        </Container>
                                    )}
                                    {/* add input fields for name, email, phone, and cell phone */}
                                    <Container direction="column">
                                        <TextField
                                            error={doValidate && name === ''}
                                            helperText={
                                                doValidate && name === ''
                                                    ? 'Required'
                                                    : ''
                                            }
                                            variant="outlined"
                                            value={name}
                                            onChange={(e) =>
                                                setName(e.target.value)
                                            }
                                            label="Name"
                                            style={{ ...inputStyle }}
                                            disabled={
                                                !hasPermission(
                                                    workspaceUser,
                                                    EDIT_USER,
                                                )
                                            }
                                            size="small"
                                        />
                                        <TextField
                                            error={
                                                doValidate &&
                                                (email === '' ||
                                                    !isEmailValid(email))
                                            }
                                            helperText={emailHelperText(
                                                doValidate,
                                                email,
                                            )}
                                            variant="outlined"
                                            value={email}
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                            label="Email"
                                            style={inputStyle}
                                            size="small"
                                            disabled={!disableEmail}
                                        />
                                        <TextField
                                            variant="outlined"
                                            value={cellPhone}
                                            onChange={(e) => {
                                                setCellPhone(e.target.value)
                                                setCellPhoneError(false)
                                            }}
                                            label="Cell Phone"
                                            error={cellPhoneError}
                                            helperText={
                                                cellPhoneError
                                                    ? INVALID_PHONE_MESSAGE
                                                    : ''
                                            }
                                            style={{ ...inputStyle, flex: 1 }}
                                            size="small"
                                            disabled={
                                                !hasPermission(
                                                    workspaceUser,
                                                    EDIT_USER,
                                                )
                                            }
                                        />
                                        {/* </Container> */}
                                        <TextField
                                            variant="outlined"
                                            value={phone}
                                            onChange={(e) => {
                                                setPhone(e.target.value)
                                                setPhoneError(false)
                                            }}
                                            label="Phone"
                                            error={phoneError}
                                            helperText={
                                                phoneError
                                                    ? INVALID_PHONE_MESSAGE
                                                    : ''
                                            }
                                            style={inputStyle}
                                            size="small"
                                            disabled={
                                                !hasPermission(
                                                    workspaceUser,
                                                    EDIT_USER,
                                                )
                                            }
                                        />
                                        <Container
                                            direction="column"
                                            style={{
                                                margin: theme.spacing(
                                                    1,
                                                    2,
                                                    1,
                                                    2,
                                                ),
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                    marginBottom: theme.spacing(
                                                        0.5,
                                                    ),
                                                }}
                                            >
                                                Expiration Date
                                            </Typography>
                                            <TextField
                                                variant="outlined"
                                                type="date"
                                                value={autoArchiveDate}
                                                onChange={(e) =>
                                                    setAutoArchiveDate(
                                                        e.target.value,
                                                    )
                                                }
                                                size="small"
                                                fullWidth
                                                disabled={
                                                    !hasPermission(
                                                        workspaceUser,
                                                        EDIT_USER,
                                                    )
                                                }
                                                InputProps={{
                                                    endAdornment: autoArchiveDate ? (
                                                        <InputAdornment position="end">
                                                            <Tooltip title="Clear date">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        setAutoArchiveDate(
                                                                            '',
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        !hasPermission(
                                                                            workspaceUser,
                                                                            EDIT_USER,
                                                                        )
                                                                    }
                                                                >
                                                                    <ClearIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    ) : undefined,
                                                }}
                                                helperText="Leave blank to never auto expire"
                                            />
                                        </Container>

                                        {workspaceUser?.active_workspace
                                            ?.company_type === 'APARTMENT' && (
                                            <Container
                                                direction="column"
                                                style={{
                                                    margin: theme.spacing(
                                                        2,
                                                        2,
                                                        1,
                                                        2,
                                                    ),
                                                    padding: theme.spacing(
                                                        1.5,
                                                        2,
                                                    ),
                                                    backgroundColor:
                                                        theme.palette.grey[50],
                                                    borderRadius:
                                                        theme.shape
                                                            .borderRadius,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={
                                                                markAsPropertyManager
                                                            }
                                                            onChange={(
                                                                _,
                                                                checked,
                                                            ) =>
                                                                setMarkAsPropertyManager(
                                                                    checked,
                                                                )
                                                            }
                                                            disabled={
                                                                !hasPermission(
                                                                    workspaceUser,
                                                                    EDIT_USER,
                                                                )
                                                            }
                                                            color="primary"
                                                        />
                                                    }
                                                    label={
                                                        <Typography
                                                            variant="body2"
                                                            style={{
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            Mark as Property
                                                            Manager
                                                        </Typography>
                                                    }
                                                />
                                                {workspaceUser?.active_workspace
                                                    ?.property_manager && (
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        style={{
                                                            color:
                                                                theme.palette
                                                                    .text
                                                                    .secondary,
                                                            marginTop: theme.spacing(
                                                                0.5,
                                                            ),
                                                            marginLeft: theme.spacing(
                                                                4,
                                                            ),
                                                            fontStyle: 'italic',
                                                            minHeight: 20,
                                                        }}
                                                    >
                                                        This will replace{' '}
                                                        {
                                                            workspaceUser
                                                                .active_workspace
                                                                .property_manager
                                                                .name
                                                        }{' '}
                                                        as Property Manager.
                                                    </Typography>
                                                )}
                                            </Container>
                                        )}
                                        {workspaceUser?.active_workspace
                                            ?.company_type === 'VENDOR' && (
                                            <Container
                                                direction="column"
                                                style={{
                                                    margin: theme.spacing(
                                                        2,
                                                        2,
                                                        1,
                                                        2,
                                                    ),
                                                    padding: theme.spacing(
                                                        1.5,
                                                        2,
                                                    ),
                                                    backgroundColor:
                                                        theme.palette.grey[50],
                                                    borderRadius:
                                                        theme.shape
                                                            .borderRadius,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                }}
                                            >
                                                <Tooltip title="When marked, this sub vendor can only see work that has been assigned to them.">
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={
                                                                    markAsSubVendor
                                                                }
                                                                onChange={(
                                                                    _,
                                                                    checked,
                                                                ) => {
                                                                    if (
                                                                        isInvoiceCodeValidated
                                                                    ) {
                                                                        setMarkAsSubVendor(
                                                                            checked,
                                                                        )
                                                                    } else {
                                                                        setPendingSubVendorValue(
                                                                            checked,
                                                                        )
                                                                        setShowInvoiceCodeDialog(
                                                                            true,
                                                                        )
                                                                    }
                                                                }}
                                                                disabled={
                                                                    !hasPermission(
                                                                        workspaceUser,
                                                                        EDIT_USER,
                                                                    )
                                                                }
                                                                color="primary"
                                                            />
                                                        }
                                                        label={
                                                            <Typography
                                                                variant="body2"
                                                                style={{
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                Mark as Sub
                                                                Vendor
                                                            </Typography>
                                                        }
                                                    />
                                                </Tooltip>
                                            </Container>
                                        )}
                                        {drawerUser && (
                                            <TextField
                                                variant="outlined"
                                                value={
                                                    date !== null
                                                        ? toMMDDYYYY(
                                                              new Date(date),
                                                          )
                                                        : ''
                                                }
                                                label="Created Date"
                                                style={{
                                                    ...inputStyle,
                                                    cursor: 'pointer',
                                                }}
                                                size="small"
                                                disabled={true}
                                                InputProps={{
                                                    endAdornment: (
                                                        <CalendarTodayOutlined fontSize="small" />
                                                    ),
                                                }}
                                            />
                                        )}
                                    </Container>

                                    <Divider
                                        style={{
                                            margin: theme.spacing(1, 2, 0, 2),
                                        }}
                                    />
                                    <Container style={{ flex: 1 / 2 }} />
                                </Container>

                                <Container
                                    direction="column"
                                    style={{
                                        marginLeft: theme.spacing(8),
                                        width: '300px',
                                    }}
                                >
                                    {/* add and remove user permission roles */}

                                    <Container
                                        style={{
                                            marginBottom: theme.spacing(1),
                                            fontWeight:
                                                theme.typography.fontWeightBold,
                                            fontSize: 18,
                                        }}
                                    >
                                        Permission Role
                                    </Container>
                                    <Container
                                        ref={roleListRef}
                                        direction="column"
                                        style={{ maxHeight: '600px' }}
                                        scrollY
                                    >
                                        {renderPermissionRoles(permissionRoles)}
                                    </Container>

                                    {/* footer */}
                                    <Divider />
                                </Container>
                            </Container>
                            <Container
                                style={{
                                    padding: theme.spacing(2),
                                    justifyContent: 'flex-end',
                                }}
                            >
                                {drawerUser && !drawerUser.workspace_owner && (
                                    <Button
                                        variant="outlined"
                                        style={{
                                            color: theme.palette.primary.dark,
                                            textTransform: 'none',
                                        }}
                                        onClick={() => {
                                            if (
                                                confirm(
                                                    `Doing this will remove ${
                                                        drawerUser.id ===
                                                        workspaceUser?.id
                                                            ? 'you'
                                                            : drawerUser.name
                                                    } from ${
                                                        workspaceUser
                                                            ?.active_workspace
                                                            .name
                                                    }${
                                                        drawerUser.id ===
                                                        workspaceUser?.id
                                                            ? ' and log you out'
                                                            : ''
                                                    }. Are you sure you want to do this?`,
                                                )
                                            ) {
                                                dispatch(
                                                    archiveUser({
                                                        user: drawerUser,
                                                    }),
                                                )
                                                if (
                                                    drawerUser.id ===
                                                    workspaceUser?.id
                                                ) {
                                                    logout()
                                                } else {
                                                    toast.success(
                                                        `${drawerUser.name} was removed from your workspace!`,
                                                    )
                                                }
                                                handleClose()
                                            }
                                        }}
                                    >
                                        Archive
                                    </Button>
                                )}
                                {drawerUser && (
                                    <Button
                                        color="secondary"
                                        style={{
                                            marginRight: theme.spacing(3),
                                            marginLeft: theme.spacing(3),
                                            textTransform: 'none',
                                        }}
                                        variant="outlined"
                                        onClick={handleClose}
                                    >
                                        Close
                                    </Button>
                                )}
                                <Container>
                                    <Button
                                        variant={'outlined'}
                                        onClick={() => saveButtonClick()}
                                        style={{
                                            color: drawerUser
                                                ? 'black'
                                                : theme.palette.primary.dark,
                                            textTransform: 'none',
                                            minWidth: '80px',
                                        }}
                                        disabled={!canSave || isLoading}
                                    >
                                        {isLoading ? (
                                            <CircularProgress size={24} />
                                        ) : drawerUser ? (
                                            'Update'
                                        ) : (
                                            'Save'
                                        )}
                                    </Button>
                                </Container>
                            </Container>
                        </Container>
                    </Paper>
                </Slide>
            </Modal>
            <Dialog
                open={showInvoiceCodeDialog}
                onClose={() => setShowInvoiceCodeDialog(false)}
            >
                <DialogContent>
                    <DialogContentText>
                        Please enter your invoice code to confirm this change.
                    </DialogContentText>
                    <VendorInvoiceCodeGate
                        layout="dialog"
                        onValidated={() => {
                            setMarkAsSubVendor(pendingSubVendorValue)
                            setShowInvoiceCodeDialog(false)
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowInvoiceCodeDialog(false)}
                        style={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
